import { Sparkles } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import tips from "../../../../assets/copy/tips";
import AutoSuggestion from "../../../../components/AutoSuggestion";
import ErrorToast from "../../../../components/ErrorToast";
import EditorListItems, {
	type EditorListItem,
} from "../../../../components/ResumeBuilder/EditorListItems/EditorListItems";
import SectionActions from "../../../../components/ResumeBuilder/SectionActions";
import SkipSectionDialog from "../../../../components/ResumeBuilder/SkipSectionDialog";
import Spinner from "../../../../components/Spinner";
import TextEditor from "../../../../components/TextEditor";
import Tips from "../../../../components/Tips";
import InputField from "../../../../components/UIComponents/InputField";
import SelectField from "../../../../components/UIComponents/SelectField";
import { Button } from "../../../../components/ui/button";
import { Label } from "../../../../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../components/ui/popover";
import ResumeContext from "../../../../context/ResumeContext";
import { NavigationHandler } from "../../../../handlers/navigationHandler";
import { resumeObjectToContextFormat } from "../../../../handlers/valueTransformation";
import { Education } from "../../../../models/resume/education";
import { nextAfterEducation, useEducationFirst } from "../../../../router/sectionOrder";
import { educationSchema, firstFieldErrors } from "../../../../schemas/resume";
import { StaticValue } from "../../../../seed/constants";
import options from "../../../../seed/dropDownOptions";
import mapping from "../../../../seed/metadata/filemapping";
import { usePatchResumeSection } from "../../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../../services/create-resume/resumeSections";
import type { ResumeContextValue } from "../../../../types/resume";

interface EducationFormState {
	schoolName: string;
	educationLevel: string;
	fieldOfStudyCategory: string;
	fieldOfStudy: string;
	gMonth: string | number;
	gYear: string | number;
	referenceId?: string;
}

/** One message per field, in the same names the schema reports them under. */
type EducationFieldErrors = Partial<
	Record<
		| "schoolName"
		| "schoolLocation"
		| "country"
		| "educationLevel"
		| "fieldOfStudy"
		| "fieldOfStudyCategory"
		| "gMonth"
		| "gYear",
		string
	>
>;

const ResumeEducationInformation = () => {
	const navigate = useNavigate();
	const eduInfo = useSelector(
		(state: { EducationInfoReducer: { eduInfoList: EditorListItem[]; description: string } }) =>
			state.EducationInfoReducer,
	);
	const eduInfoList = eduInfo.eduInfoList;
	const [isInputCompleted, setIsInputCompleted] = useState(false);
	const navigationHandler = new NavigationHandler();
	const link = useLocation().pathname.split("/")[2];
	const patchResumeSection = usePatchResumeSection();
	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();
	const [cookies] = useCookies(["resume"]);
	const [country, setCountry] = useState("Tanzania");

	const locations = mapping["districtsRegions"][0].districtRegions;

	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;
	let { journal } = resumeData;

	let resumeCurrentStatus = journal ? journal.currentStatus : false;

	let isComplete = resumeCurrentStatus == "COMPLETED";

	const [showSkipDialog, setShowSkipDialog] = useState(false);

	//Declaring the form states
	const [educationInfo, setEducationInfo] = useState<EducationFormState>({
		schoolName: "",
		educationLevel: "",
		fieldOfStudyCategory: "",
		fieldOfStudy: "",
		gMonth: "",
		gYear: "",
	});
	const [schoolLocation, setSchoolLocation] = useState("");

	const [editorListItems, setEditorListItems] = useState(eduInfoList);

	const [description, setDescription] = useState(eduInfo.description);
	const navigationState = useLocation().state;

	useEffect(() => {
		if (navigationState) {
			const currentEducation = resumeData.education.filter(
				(item) => item.referenceId === navigationState.referenceId,
			)[0];
			if (navigationState) {
				if (resumeData.education.length > 0) {
					setEducationInfo({
						schoolName: currentEducation.schoolName,
						educationLevel: currentEducation.educationLevel,
						fieldOfStudy: currentEducation.fieldOfStudy,
						gMonth: currentEducation.gMonth,
						gYear: currentEducation.gYear,
						referenceId: currentEducation.referenceId,
						fieldOfStudyCategory: currentEducation.fieldOfStudyCategory,
					});
					setDescription(currentEducation.description);
					// Read the parts, never `schoolCity`. That string has the empty parts
					// removed, so splitting it slid the country into the region box and
					// blanked the country — leaving the entry incomplete after an edit
					// that looked like it had changed nothing. The Set drops the copy the
					// save writes when only one of city and region was given, so editing
					// shows back exactly what was typed.
					setSchoolLocation([...new Set([currentEducation.city, currentEducation.region].filter(Boolean))].join(", "));
					setCountry(currentEducation.country || "");
				}
			}
		}
	}, [resumeData]);

	// Every problem is reported on the field that caused it. `description` is not
	// part of the schema, so its own check keeps its message next to the editor.
	const [fieldErrors, setFieldErrors] = useState<EducationFieldErrors>({});
	const [descriptionError, setDescriptionError] = useState("");

	const clearFieldError = (field: keyof EducationFieldErrors) =>
		setFieldErrors((previous) => {
			if (previous[field] === undefined) return previous;
			return { ...previous, [field]: undefined };
		});

	const updateDescription = (value: string) => {
		setDescriptionError("");
		setDescription(value);
	};

	//The form event handler
	const onChangeInput = (e) => {
		clearFieldError(e.target.name);
		if (e.target.name === "schoolLocation") {
			setSchoolLocation(e.target.value);
		} else if (e.target.name === "country") {
			setCountry(e.target.value);
		} else {
			setEducationInfo((prevState) => {
				return {
					...prevState,
					[e.target.name]: e.target.value,
				};
			});
		}
	};

	const formInputs = {
		...educationInfo,
		description,
		schoolLocation,
		country,
	};

	// Derive completeness from the stable state values, not the `formInputs`
	// object literal (which is recreated every render and would make this effect
	// run on every render).
	useEffect(() => {
		const { schoolName, educationLevel, fieldOfStudy, gMonth, gYear, fieldOfStudyCategory } = educationInfo;

		setIsInputCompleted(
			educationSchema.safeParse({
				schoolName,
				educationLevel,
				fieldOfStudy,
				gMonth,
				gYear,
				fieldOfStudyCategory,
				schoolLocation,
				country,
			}).success,
		);
	}, [educationInfo, schoolLocation, country]);

	// Where "Skip for now" goes: Work History for a reader with no experience, Skills
	// for everyone else, who was asked for work history before reaching this screen.
	const educationFirst = useEducationFirst();
	const sectionCounts = {
		educationCount: resumeData.education.length,
		workHistoryCount: resumeData.workHistory.length,
	};
	const skipDestination = nextAfterEducation(educationFirst, sectionCounts);

	const skipSection = () => {
		navigationHandler.getNext(navigationHandler.template.navigationFlow, link, isComplete);
		navigate("/resume/education-summary");
	};

	const [serverErrors, setServerErrors] = useState([]);

	function patchSection() {
		const {
			schoolName,
			schoolLocation,
			educationLevel,
			fieldOfStudy,
			fieldOfStudyCategory,
			gMonth,
			gYear,
			description,
		} = formInputs;

		// The schema checked this exact string, not a concatenation of it with the
		// country, so what was validated is what gets saved.
		//
		// Either part alone is accepted, but the backend calls a section complete only
		// when city, region and country are all filled, and that rule is not ours to
		// change. A single part therefore goes into both slots instead of leaving a
		// blank that would keep Education unfinished with nothing on screen to explain
		// it. The display join drops the repeat.
		const [first = "", second = ""] = schoolLocation.split(",").map((part) => part.trim());

		const payload = {
			city: first || second,
			region: second || first,
			country: country,
			schoolName,
			schoolLocation,
			educationLevel,
			fieldOfStudy,
			fieldOfStudyCategory,
			gMonth,
			gYear,
			description,
		};

		const validation = educationSchema.safeParse(formInputs);
		if (validation.success) {
			const _section = "EDUCATION";
			const eduPayload = new Education(payload);
			const eduReq = eduPayload.getEducationRequest();

			if (educationInfo.referenceId !== "") {
				eduReq.referenceId = educationInfo.referenceId;
			}

			const resumeId = cookies[staticValues.localStorageResumeId];

			patchResumeSection.mutate({ resumeId, section: RESUME_SECTIONS.EDUCATION, data: eduReq } as never, {
				onSuccess: successPatchEducation,
				onError: errorPatchEducation,
			});
		} else {
			setFieldErrors(firstFieldErrors(validation.error));
		}
	}

	function nextButton(e) {
		e.preventDefault();
		setServerErrors([]);

		if (description.includes("{") || description.includes("}")) {
			return setDescriptionError("Please edit the suggested entries on the description");
		}
		setDescriptionError("");

		// `patchSection` parses the schema itself and reports each problem on the
		// field that caused it, so there is nothing to check twice here.
		return patchSection();
	}

	function successPatchEducation(ledger) {
		resumeObjectToContextFormat(ledger, setResumeData);

		navigate("/resume/education-summary");
	}

	function errorPatchEducation(data) {
		const serverResponse = data.response.data;
		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...serverResponse.message];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message];
				});
	}

	useEffect(() => {
		if (educationInfo.educationLevel === "O-Level" || educationInfo.educationLevel === "A-Level") {
			setEducationInfo((prev) => {
				return {
					...prev,
					fieldOfStudyCategory: "Secondary Education",
				};
			});
		}
	}, [educationInfo.educationLevel]);

	return (
		<>
			<SkipSectionDialog open={showSkipDialog} onOpenChange={setShowSkipDialog} onSkip={skipSection} />

			<div className="min-h-screen">
				{/* Server-side failures only. Field-level problems are reported inline
				    on the field that caused them, never here. */}
				{serverErrors.map((item, index) => {
					return <ErrorToast error={item} key={index} />;
				})}
				<div>
					<div className="mb-3 flex items-start justify-between gap-3">
						<p>List your academic credentials, starting with the most recent degree.</p>
						<Tips content={tips.educationHistory} />
					</div>
					<form action="">
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div>
								<SelectField
									name="educationLevel"
									value={String(educationInfo.educationLevel)}
									onChange={onChangeInput}
									options={options.educationLevels}
									label="Education Levels"
									error={fieldErrors.educationLevel}
								/>
							</div>

							<div>
								<SelectField
									name="fieldOfStudyCategory"
									value={educationInfo.fieldOfStudyCategory}
									onChange={onChangeInput}
									options={options.fieldsOfStudy}
									label="Field of Study"
									error={fieldErrors.fieldOfStudyCategory}
								/>
							</div>

							<div>
								<InputField
									type="text"
									label="Degree/Certificate Major/Combination"
									name="fieldOfStudy"
									value={educationInfo.fieldOfStudy}
									onChange={onChangeInput}
									placeholder="Ex: BSc, Computer Science"
									error={fieldErrors.fieldOfStudy}
								/>
							</div>

							<div>
								<InputField
									type="text"
									label="School Name"
									name="schoolName"
									value={educationInfo.schoolName}
									onChange={onChangeInput}
									placeholder="Enter School Name"
									error={fieldErrors.schoolName}
								/>
							</div>

							<div>
								{/* The placeholder carries the empty value on purpose: a <select> whose
								    value matches no option falls back to displaying the first one, so an
								    unset country read as "Tanzania" while the state behind it was "". */}
								<SelectField
									name="country"
									value={country}
									onChange={onChangeInput}
									options={[{ value: "", name: "Select country" }, ...options.phoneCountryCodeOptions]}
									label="Country"
									error={fieldErrors.country}
								/>
							</div>

							<div className="relative">
								{!(country === "Tanzania") && (
									<InputField
										type="text"
										label="Ex: City, Region"
										name="schoolLocation"
										value={schoolLocation}
										onChange={onChangeInput}
										placeholder="City, Region"
										error={fieldErrors.schoolLocation}
									/>
								)}
								{country === "Tanzania" && (
									<AutoSuggestion
										suggestions={locations}
										label="Eg: District, Region"
										type="text"
										name="schoolLocation"
										userInput={schoolLocation}
										// Not a change event, so the message has to be cleared by hand
										// when a district is picked from the list.
										setUserInput={(value) => {
											clearFieldError("schoolLocation");
											setSchoolLocation(value);
										}}
										isForm={true}
										error={fieldErrors.schoolLocation}
									/>
								)}
							</div>

							<div>
								<SelectField
									name="gMonth"
									value={String(educationInfo.gMonth)}
									onChange={onChangeInput}
									options={options.months}
									label="Graduation Month"
									error={fieldErrors.gMonth}
								/>
							</div>

							<div>
								<SelectField
									name="gYear"
									value={String(educationInfo.gYear)}
									onChange={onChangeInput}
									options={options.years(40)}
									label="Graduation Year"
									error={fieldErrors.gYear}
								/>
							</div>
							<div className="md:col-span-2">
								<div className="mb-1 flex items-center justify-between gap-3">
									<Label className="text-sm">Activities or Relevant Course Work</Label>
									{/* The suggestions used to sit in a second column beside the editor,
									    which is what forced this step into an 80vw dialog. In a popover
									    they cost the page no width, so the editor can live on the form. */}
									<Popover>
										<PopoverTrigger asChild>
											<button
												type="button"
												className="flex cursor-pointer items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1.5 font-semibold text-primary-700 text-sm"
											>
												<Sparkles className="size-4" />
												Get help with writing
											</button>
										</PopoverTrigger>
										<PopoverContent align="end" className="w-[min(32rem,92vw)] p-2">
											<p className="px-1 text-muted-foreground text-xs">
												Pick the activities and coursework that apply. They are added to your description.
											</p>
											<EditorListItems
												editorListItems={editorListItems}
												editorContent={description}
												setEditorContent={updateDescription}
												setEditorListItems={setEditorListItems}
												className="h-auto max-h-[60vh]"
											/>
										</PopoverContent>
									</Popover>
								</div>
								<TextEditor
									value={description}
									setValue={updateDescription}
									placeholder="Add a description."
									className="text-editor-auto-grow"
								/>
								{descriptionError && (
									<div className="mt-1 font-medium text-red-500 text-sm leading-tight">{descriptionError}</div>
								)}
							</div>

							{/* `md:` matters: an unprefixed span of 2 opens a second, implicit column
							    on the one-column phone grid, which the fields then flow across two at
							    a time — each pair overlapping in a track sized to nothing. */}
							{/* No back button: "Skip for now" already walks the reader forward past
							    this section, and a third choice beside it only asked them to pick a
							    direction they had not asked to change. */}
							<SectionActions className="flex items-center justify-end gap-3 md:col-span-2">
								<div className="flex items-center gap-3">
									{/* Skipping education lands on the next section in the flow, which
									    depends on the experience level — it used to always jump to
									    skills, stepping over work history for a reader with no jobs. */}
									<Button type="button" variant="ghost" onClick={() => navigate(`/resume/${skipDestination}`)}>
										Skip for now
									</Button>
									<Button
										type="button"
										onClick={(e) => {
											if (!isInputCompleted) {
												setShowSkipDialog(true);
											}
											nextButton(e);
										}}
									>
										{patchResumeSection.isPending ? (
											<>
												Loading <Spinner />
											</>
										) : (
											"Continue"
										)}
									</Button>
								</div>
							</SectionActions>
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default ResumeEducationInformation;
